import {
  type BaseQueryFn,
  createApi,
  retry,
} from '@reduxjs/toolkit/query/react'
import { type AxiosError, type AxiosRequestConfig } from 'axios'
import { toast } from 'sonner'
import config from '@/config'
import { customAxios } from '@/utils/axiosHelper'
import {
  type ServiceCursorPaginatedResponseType,
  ServiceErrorHandler,
} from '@/connect-types/backend/service'
import { buildUrl } from '@/utils/common'
import { type JobType, type PartialJobType } from '@/pages/admin/queue/failed'
import { shouldBailout } from '../helpers'

const axiosBaseQuery =
  (
    { baseUrl }: { baseUrl: string } = { baseUrl: '' }
  ): BaseQueryFn<{
    url: string
    method: AxiosRequestConfig['method']
    data?: AxiosRequestConfig['data']
    params?: AxiosRequestConfig['params']
  }> =>
  async ({ url, method, data, params }) => {
    try {
      const result = await customAxios({
        url: baseUrl + url,
        method,
        data,
        params,
      })

      return { data: result.data }
    } catch (axiosError) {
      const err = axiosError as AxiosError

      if ((method ?? '').toUpperCase() !== 'GET') {
        ServiceErrorHandler(axiosError)
      }

      if (shouldBailout(err)) {
        retry.fail(err)
      }

      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      }
    }
  }

export interface WhatsAppAccount {
  id: string
  organisation_id: string
  is_default: boolean
  whatsapp_business_account_id: string
  phone_number_id: string
  display_phone_number: string | null
  verified_name: string | null
  connected_at: string
}

export interface WhatsAppConfig {
  app_id: string | null
  config_id: string | null
  graph_api_version: string
}

export interface ConnectWhatsAppBody {
  code: string
  whatsapp_business_account_id: string
  phone_number_id: string
}

const cypherApi = createApi({
  reducerPath: 'cypher',
  baseQuery: retry(axiosBaseQuery({ baseUrl: config.url.cypher }), {
    maxRetries: 2,
  }),

  tagTypes: ['FailedJobs', 'WhatsApp'],

  endpoints: (build) => ({
    failedJobs: build.query<
      ServiceCursorPaginatedResponseType<PartialJobType>,
      { cursor: string; queue?: string }
    >({
      query: ({ queue, cursor }) => ({
        url: buildUrl('/admin/failed-jobs', { queue, cursor }),
        method: 'GET',
      }),
      providesTags: [{ type: 'FailedJobs', id: 'LIST' }],
    }),

    failedJob: build.query<JobType, { id: string }>({
      query: ({ id }) => ({
        url: `/admin/failed-jobs/${id}`,
        method: 'GET',
      }),
      providesTags: ({ uuid }) => [{ type: 'FailedJobs', id: uuid }],
    }),

    requeueFailedJob: build.mutation<{ output: string }, { id: string }>({
      query: ({ id }) => ({
        url: `/admin/failed-jobs/${id}/requeue`,
        method: 'PUT',
      }),
      transformResponse: (item: { output: string }) => {
        toast.success(item?.output)
        return item
      },
      invalidatesTags: (_1, _2, { id }) => [
        { type: 'FailedJobs', id },
        { type: 'FailedJobs', id: 'LIST' },
      ],
    }),

    batchRequeueFailedJobs: build.mutation<
      { output: string },
      { failed_jobs: string[] }
    >({
      query: ({ failed_jobs }) => ({
        url: `/admin/failed-jobs/requeue`,
        method: 'POST',
        data: { failed_jobs },
      }),
      transformResponse: (item: { output: string }) => {
        toast.success(item?.output)
        return item
      },
      invalidatesTags: (_1, _2, _3) => [{ type: 'FailedJobs', id: 'LIST' }],
    }),

    deleteFailedJob: build.mutation<JobType, { id: string }>({
      query: ({ id }) => ({
        url: `/admin/failed-jobs/${id}`,
        method: 'DELETE',
      }),
      transformResponse: ({ output }) => {
        toast.success(output)
        return output
      },
      invalidatesTags: (_1, _2, { id }) => [
        { type: 'FailedJobs', id },
        { type: 'FailedJobs', id: 'LIST' },
      ],
    }),

    whatsAppAccounts: build.query<WhatsAppAccount[], { orgId: string }>({
      query: ({ orgId }) => ({ url: `/${orgId}/whatsapp`, method: 'GET' }),
      transformResponse: (res: { data: WhatsAppAccount[] }) => res.data,
      providesTags: [{ type: 'WhatsApp', id: 'LIST' }],
    }),

    whatsAppConfig: build.query<WhatsAppConfig, { orgId: string }>({
      query: ({ orgId }) => ({
        url: `/${orgId}/whatsapp/config`,
        method: 'GET',
      }),
      transformResponse: (res: { data: WhatsAppConfig }) => res.data,
    }),

    connectWhatsApp: build.mutation<
      WhatsAppAccount,
      { orgId: string; body: ConnectWhatsAppBody }
    >({
      query: ({ orgId, body }) => ({
        url: `/${orgId}/whatsapp`,
        method: 'POST',
        data: body,
      }),
      transformResponse: (res: { data: WhatsAppAccount }) => res.data,
      invalidatesTags: [{ type: 'WhatsApp', id: 'LIST' }],
    }),

    setDefaultWhatsApp: build.mutation<
      WhatsAppAccount,
      { orgId: string; id: string }
    >({
      query: ({ orgId, id }) => ({
        url: `/${orgId}/whatsapp/${id}/default`,
        method: 'PUT',
      }),
      transformResponse: (res: { data: WhatsAppAccount }) => res.data,
      invalidatesTags: [{ type: 'WhatsApp', id: 'LIST' }],
    }),

    disconnectWhatsApp: build.mutation<void, { orgId: string; id: string }>({
      query: ({ orgId, id }) => ({
        url: `/${orgId}/whatsapp/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'WhatsApp', id: 'LIST' }],
    }),
  }),
})

export default cypherApi
